import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import { sumBy } from 'lodash/fp'

import { MINUTE } from '@/core/constants/constants'

import type { BaseUser } from './types'
import type { UserStatus } from './users'
import { createApiMethod, type } from './utils/createApiMethod'
import { stripEmptyQueryParams } from './utils/stripEmptyQueryParams'

export type UserGroup = {
  id: string
  name: string
  created_at: string
  creator: {
    email: string
    user_id: string
  }
  num_students: number
  total_members: number
  is_private: boolean
}

export type UserGroupsSort =
  | 'user_group_name'
  | 'visibility'
  | 'members'
  | 'creation_date'

export type UserGroupsOrder = 'asc' | 'desc'

export type UserGroupsQuery = {
  page?: number
  size?: number
  name?: string
  sort_by?: UserGroupsSort
  sort_order?: UserGroupsOrder
  creator_id?: string
  creator_name?: string
  from_created_at?: string
  to_created_at?: string
  private?: boolean
  members_ids?: string[]
  exclude_groups_with_members_ids?: string[]
}
export type UserGroupsCandidatesQuery = {
  query?: string
  size?: number
  offset?: number
  states?: UserStatus[]
  department_id?: string
  license_provider_id?: string
}

export const usersGroups = {
  list: createApiMethod({
    method: 'GET',
    path: '/users/groups',
    query: type<UserGroupsQuery>(),
    response: type<{
      user_groups: UserGroup[]
      total_user_groups: number
    }>(),
    config: { paramsSerializer: { indexes: null } },
  }),

  create: createApiMethod({
    method: 'POST',
    path: '/users/groups',
    data: type<{
      user_group_name: string
      private: boolean
    }>(),
  }),

  bulkDelete: createApiMethod({
    method: 'DELETE',
    path: '/users/groups',
    data: type<{ user_groups_ids: string[] }>(),
    response: type<{ errors: Record<string, string> }>(),
  }),

  members: {
    create: createApiMethod({
      method: 'POST',
      path: '/users/groups/members',
      data: type<{
        user_group_id: string
        users_ids: string[]
      }>(),
      response: type<{ errors: Record<string, string> }>(),
    }),

    delete: createApiMethod({
      method: 'DELETE',
      path: '/users/groups/members',
      data: type<{
        user_group_id: string
        users_ids: string[]
      }>(),
      response: type<{ errors: Record<string, string> }>(),
    }),
  },

  userGroup: {
    update: createApiMethod({
      method: 'PUT',
      path: '/users/groups/:groupId',
      data: type<{ user_group_name: string }>(),
    }),

    privacy: {
      update: createApiMethod({
        method: 'PUT',
        path: '/users/groups/:groupId/privacy',
        data: type<{ private: boolean }>(),
      }),
    },

    candidates: {
      list: createApiMethod({
        method: 'GET',
        path: '/users/groups/:groupId/candidates',
        response: type<{ candidates: BaseUser[]; total_candidates: number }>(),
        query: type<UserGroupsCandidatesQuery>(),
        config: { paramsSerializer: { indexes: null } },
      }),
    },

    members: {
      create: createApiMethod({
        method: 'POST',
        path: '/users/groups/:groupId/members',
        data: type<{ user_id: string }>(),
      }),

      delete: createApiMethod({
        method: 'DELETE',
        path: '/users/groups/:groupId/members/:userId',
      }),

      list: createApiMethod({
        method: 'GET',
        path: '/users/groups/:groupId/members',
        response: type<{ members: BaseUser[] }>(),
        query: type<{
          query?: string
          states?: UserStatus[]
          department_id?: string
          license_provider_id?: string
        }>(),
        config: { paramsSerializer: { indexes: null } },
      }),
    },
  },
}

export const userGroupQueries = {
  all: () => ['user-group'] as const,
  lists: () => ({
    queryKey: [...userGroupQueries.all(), 'lists'],
  }),
  list: (searchQuery?: UserGroupsQuery) => {
    const query = stripEmptyQueryParams(searchQuery)

    return queryOptions({
      queryKey: [...userGroupQueries.lists().queryKey, query] as const,
      queryFn: () => usersGroups.list({ query }),
      staleTime: MINUTE * 5,
    })
  },
  infiniteLists: () => ({
    queryKey: [...userGroupQueries.lists().queryKey, 'infinite'] as const,
  }),
  infiniteList: (searchQuery?: UserGroupsQuery) => {
    const query = stripEmptyQueryParams({
      size: 25,
      ...searchQuery,
    })

    return infiniteQueryOptions({
      queryKey: [...userGroupQueries.infiniteLists().queryKey, query] as const,
      queryFn: ({ pageParam }) =>
        usersGroups.list({ query: { ...query, page: pageParam } }),
      getNextPageParam: (lastPage, allPages, lastPageParam) => {
        const loaded = sumBy((page) => page.user_groups.length, allPages)
        const hasMore =
          lastPage.user_groups.length > 0 && loaded < lastPage.total_user_groups
        return hasMore ? lastPageParam + 1 : undefined
      },
      initialPageParam: 0,
      staleTime: MINUTE * 5,
      placeholderData: (previousData) => previousData,
    })
  },
  candidatesLists: () => ({
    queryKey: [...userGroupQueries.all(), 'candidates-list'] as const,
  }),
  candidatesList: (groupId: string, params?: UserGroupsCandidatesQuery) =>
    infiniteQueryOptions({
      queryKey: [
        ...userGroupQueries.candidatesLists().queryKey,
        groupId,
        params,
      ] as const,
      queryFn: ({ pageParam = 0 }) =>
        usersGroups.userGroup.candidates.list({
          params: { groupId },
          query: {
            ...params,
            offset: pageParam,
          },
        }),
      getNextPageParam: (lastPage, allPages) => {
        // The backend offset indexes the source pool that is scanned for
        // matches (filtered results are sparse across offset windows), so the
        // next offset must advance by a full requested window rather than by
        // the number of matches loaded so far — otherwise windows overlap and
        // candidates are duplicated or skipped. Keep paginating until every
        // match is loaded, but stop once a window comes back empty so an
        // over-reported total_candidates can't cause an endless refetch.
        const size = params?.size ?? 25
        const loaded = allPages.reduce(
          (sum, page) => sum + page.candidates.length,
          0,
        )
        return lastPage.candidates.length > 0 &&
          loaded < lastPage.total_candidates
          ? allPages.length * size
          : undefined
      },
      initialPageParam: 0,
      staleTime: MINUTE * 5,
    }),
  membersLists: () => ({
    queryKey: [...userGroupQueries.all(), 'members-list'] as const,
  }),
  membersList: (
    groupId: string,
    query?: string,
    states?: UserStatus[],
    departmentId?: string,
    licenseProviderId?: string,
  ) =>
    queryOptions({
      queryKey: [
        ...userGroupQueries.membersLists().queryKey,
        groupId,
        query,
        states,
        departmentId,
        licenseProviderId,
      ] as const,
      queryFn: () =>
        usersGroups.userGroup.members.list({
          params: { groupId },
          query: {
            query,
            states,
            department_id: departmentId,
            license_provider_id: licenseProviderId,
          },
        }),
      staleTime: MINUTE * 5,
    }),
}
